/**
 * Servicio para manejar notificaciones del sistema
 */
class NotificationService {
  constructor() {
    this.notifications = []
    this.listeners = []
    this.isPolling = false
    this.pollingInterval = null
  }

  /**
   * Inicia el polling de notificaciones
   */
  startPolling() {
    if (this.isPolling) return
    
    this.isPolling = true
    this.pollingInterval = setInterval(() => {
      this.checkForNewNotifications()
    }, 30000) // Verificar cada 30 segundos
  }

  /**
   * Detiene el polling de notificaciones
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
    this.isPolling = false
  }

  /**
   * Verifica si hay nuevas notificaciones
   */
  checkForNewNotifications() {
    // Simular verificación de nuevas notificaciones
    const shouldShowNotification = Math.random() < 0.3 // 30% de probabilidad
    
    if (shouldShowNotification) {
      this.generateRandomNotification()
    }
  }

  /**
   * Genera una notificación aleatoria
   */
  generateRandomNotification() {
    const notificationTypes = [
      {
        type: 'project',
        title: 'Nuevo proyecto asignado',
        icon: 'FolderIcon',
        class: 'text-primary'
      },
      {
        type: 'activity',
        title: 'Actividad completada',
        icon: 'CheckSquareIcon',
        class: 'text-success'
      },
      {
        type: 'work',
        title: 'Obra en progreso',
        icon: 'BuildingIcon',
        class: 'text-info'
      },
      {
        type: 'system',
        title: 'Mantenimiento programado',
        icon: 'SettingsIcon',
        class: 'text-warning'
      },
      {
        type: 'reminder',
        title: 'Recordatorio: Revisar documentos pendientes',
        icon: 'ClockIcon',
        class: 'text-secondary'
      },
      {
        type: 'update',
        title: 'Sistema actualizado',
        icon: 'DownloadIcon',
        class: 'text-success'
      },
      {
        type: 'meeting',
        title: 'Reunión programada para mañana',
        icon: 'CalendarIcon',
        class: 'text-info'
      },
      {
        type: 'document',
        title: 'Nuevo documento disponible',
        icon: 'FileTextIcon',
        class: 'text-primary'
      }
    ]
    
    const randomType = notificationTypes[Math.floor(Math.random() * notificationTypes.length)]
    
    const newNotification = {
      id: Date.now() + Math.random(),
      title: randomType.title,
      type: randomType.type,
      time: new Date().toISOString(),
      read: false,
    }
    
    // Agregar a la lista
    this.notifications.unshift(newNotification)
    
    // Limitar a 20 notificaciones máximo
    if (this.notifications.length > 20) {
      this.notifications = this.notifications.slice(0, 20)
    }
    
    // Notificar a los listeners
    this.notifyListeners(newNotification)
    
    return newNotification
  }

  /**
   * Obtiene todas las notificaciones
   */
  getNotifications() {
    return this.notifications
  }

  /**
   * Obtiene las notificaciones recientes (últimas 10)
   */
  getRecentNotifications() {
    return this.notifications.slice(0, 10)
  }

  /**
   * Obtiene el número de notificaciones no leídas
   */
  getUnreadCount() {
    return this.notifications.filter(n => !n.read).length
  }

  /**
   * Marca una notificación como leída
   */
  markAsRead(notificationId) {
    const notification = this.notifications.find(n => n.id === notificationId)
    if (notification) {
      notification.read = true
      this.notifyListeners()
    }
  }

  /**
   * Marca todas las notificaciones como leídas
   */
  markAllAsRead() {
    this.notifications.forEach(n => n.read = true)
    this.notifyListeners()
  }

  /**
   * Elimina una notificación
   */
  removeNotification(notificationId) {
    this.notifications = this.notifications.filter(n => n.id !== notificationId)
    this.notifyListeners()
  }

  /**
   * Limpia todas las notificaciones
   */
  clearAll() {
    this.notifications = []
    this.notifyListeners()
  }

  /**
   * Agrega un listener para cambios en las notificaciones
   */
  addListener(callback) {
    this.listeners.push(callback)
  }

  /**
   * Remueve un listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback)
  }

  /**
   * Notifica a todos los listeners
   */
  notifyListeners(notification = null) {
    this.listeners.forEach(callback => {
      try {
        callback(this.notifications, notification)
      } catch (error) {
        console.error('Error en listener de notificaciones:', error)
      }
    })
  }

  /**
   * Genera notificaciones iniciales
   */
  generateInitialNotifications() {
    // Notificación de bienvenida
    const welcomeNotification = {
      id: Date.now(),
      title: 'Bienvenido al sistema Gescom',
      type: 'system',
      time: new Date().toISOString(),
      read: false,
    }
    
    this.notifications.push(welcomeNotification)
    
    // Generar algunas notificaciones aleatorias iniciales
    setTimeout(() => {
      this.generateRandomNotification()
    }, 5000) // Primera notificación después de 5 segundos
    
    setTimeout(() => {
      this.generateRandomNotification()
    }, 15000) // Segunda notificación después de 15 segundos
    
    setTimeout(() => {
      this.generateRandomNotification()
    }, 30000) // Tercera notificación después de 30 segundos
  }
}

// Crear instancia singleton
const notificationService = new NotificationService()

export default notificationService
